/**
 * QuickBooks Connection Status Component
 *
 * Displays the QuickBooks connection status in Admin Settings.
 * Handles three states:
 * - Not Connected: shows "Connect to QuickBooks" button
 * - Connected: shows company name, token expiry, health indicator, and "Disconnect" button
 * - Expired: shows warning indicator, "Reconnect" button for expired refresh tokens
 */

import { renderIcon } from '../utils/icons';

export interface QuickBooksConnectionStatus {
  connected: boolean;
  status?: 'not_connected' | 'connected' | 'expired';
  companyName?: string;
  realmId?: string;
  accessTokenExpired?: boolean;
  refreshTokenExpired?: boolean;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
}

type ConnectionState = 'not_connected' | 'connected' | 'expired';

export interface QuickBooksConnectionStatusCallbacks {
  onConnect: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
}

/**
 * Derive connection state from status data
 */
function getConnectionState(status: QuickBooksConnectionStatus | null): ConnectionState {
  if (!status?.connected) return 'not_connected';
  if (status.refreshTokenExpired) return 'expired';
  return 'connected';
}

/**
 * Format an ISO date string for locale-aware display
 */
function formatExpiryDate(isoString: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(isoString));
}

/**
 * Render the health indicator icon based on connection state
 */
function renderHealthIndicator(state: ConnectionState): string {
  switch (state) {
    case 'connected':
      return renderIcon('checkCircle', { size: 'lg', className: 'text-success' }).outerHTML;
    case 'expired':
      return renderIcon('alertTriangle', { size: 'lg', className: 'text-warning' }).outerHTML;
    case 'not_connected':
      return renderIcon('xCircle', { size: 'lg', className: 'text-muted-foreground' }).outerHTML;
  }
}

/**
 * Render the connection status card icon background
 */
function getIconBackground(state: ConnectionState): string {
  switch (state) {
    case 'connected':
      return 'bg-success/20';
    case 'expired':
      return 'bg-warning/20';
    case 'not_connected':
      return 'bg-muted';
  }
}

/**
 * Render the status label text
 */
function getStatusLabel(state: ConnectionState): string {
  switch (state) {
    case 'connected':
      return 'Connected to QuickBooks';
    case 'expired':
      return 'Connection Expired';
    case 'not_connected':
      return 'Not Connected';
  }
}

/**
 * Render the action button(s) based on connection state
 */
function renderActionButton(state: ConnectionState): string {
  switch (state) {
    case 'connected':
      return `
        <button
          type="button"
          id="qb-disconnect-btn"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm text-destructive hover:text-destructive/80 border border-destructive/30 rounded-lg hover:bg-destructive/10 transition-colors"
        >
          ${renderIcon('x', { size: 'sm' }).outerHTML}
          <span>Disconnect</span>
        </button>
      `;
    case 'expired':
      return `
        <button
          type="button"
          id="qb-reconnect-btn"
          class="inline-flex items-center gap-2 px-4 py-2 bg-warning/20 text-warning border border-warning/30 font-medium rounded-lg hover:bg-warning/30 transition-colors"
        >
          ${renderIcon('refresh', { size: 'sm' }).outerHTML}
          <span>Reconnect</span>
        </button>
      `;
    case 'not_connected':
      return `
        <button
          type="button"
          id="qb-connect-btn"
          class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
        >
          ${renderIcon('link', { size: 'sm' }).outerHTML}
          <span>Connect to QuickBooks</span>
        </button>
      `;
  }
}

/**
 * Render the loading state
 */
export function renderQuickBooksLoading(): string {
  return `
    <div class="flex items-center justify-center py-12">
      ${renderIcon('loader', { size: 'lg', className: 'animate-spin text-primary' }).outerHTML}
      <span class="ml-3 text-muted-foreground">Loading QuickBooks status...</span>
    </div>
  `;
}

/**
 * Render the full QuickBooks connection status component
 */
export function renderQuickBooksConnectionStatus(
  status: QuickBooksConnectionStatus | null,
): string {
  const state = getConnectionState(status);

  return `
    <div class="space-y-6">
      <div>
        <h2 class="text-lg font-semibold text-foreground mb-1">QuickBooks Integration</h2>
        <p class="text-sm text-muted-foreground">
          Connect to QuickBooks Online to sync expense reports as bills.
        </p>
      </div>

      <!-- Connection Status Card -->
      <div class="glass-card p-6 rounded-lg">
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-lg ${getIconBackground(state)} flex items-center justify-center">
              ${renderHealthIndicator(state)}
            </div>
            <div>
              <h3 class="font-medium text-foreground">
                ${getStatusLabel(state)}
              </h3>
              ${state === 'connected' && status?.companyName ? `
                <p class="text-sm text-muted-foreground">${escapeHtml(status.companyName)}</p>
              ` : ''}
              ${state === 'expired' && status?.companyName ? `
                <p class="text-sm text-muted-foreground">${escapeHtml(status.companyName)}</p>
              ` : ''}
              ${state === 'not_connected' ? `
                <p class="text-sm text-muted-foreground">Connect to enable expense report syncing</p>
              ` : ''}
              ${state === 'expired' ? `
                <p class="text-sm text-warning">Refresh token has expired. Please reconnect to restore the integration.</p>
              ` : ''}
            </div>
          </div>

          <div class="flex items-center gap-2">
            ${renderActionButton(state)}
          </div>
        </div>

        ${status?.connected ? `
          <!-- Token Status -->
          <div class="mt-6 pt-6 border-t border-border">
            <h4 class="text-sm font-medium text-muted-foreground mb-3">Connection Details</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-muted-foreground">Company ID:</span>
                <span class="ml-2 font-mono text-foreground">${escapeHtml(status.realmId || 'N/A')}</span>
              </div>
              <div>
                <span class="text-muted-foreground">Access Token:</span>
                <span class="ml-2 ${status.accessTokenExpired ? 'text-warning' : 'text-success'}">
                  ${status.accessTokenExpired ? 'Expired (will auto-refresh)' : 'Valid'}
                </span>
              </div>
              ${status.accessTokenExpiresAt ? `
                <div>
                  <span class="text-muted-foreground">Access Token Expires:</span>
                  <span class="ml-2 text-foreground">${formatExpiryDate(status.accessTokenExpiresAt)}</span>
                </div>
              ` : ''}
              ${status.refreshTokenExpiresAt ? `
                <div>
                  <span class="text-muted-foreground">Refresh Token Expires:</span>
                  <span class="ml-2 ${status.refreshTokenExpired ? 'text-destructive' : 'text-foreground'}">
                    ${formatExpiryDate(status.refreshTokenExpiresAt)}
                    ${status.refreshTokenExpired ? ' (Expired - reconnection required)' : ''}
                  </span>
                </div>
              ` : ''}
            </div>
          </div>
        ` : ''}
      </div>

      <!-- Info Section -->
      <div class="glass-card p-4 rounded-lg bg-muted/30">
        <div class="flex items-start gap-3">
          ${renderIcon('info', { size: 'sm', className: 'text-primary mt-0.5' }).outerHTML}
          <div class="text-sm text-muted-foreground">
            <p class="mb-2">
              <strong class="text-foreground">About QuickBooks Integration:</strong>
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li>Sync approved expense reports to QuickBooks as bills</li>
              <li>Automatically create vendors for expense report owners</li>
              <li>Attach receipt images to QuickBooks bills</li>
              <li>Track sync status for each expense report</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Attach event listeners to the rendered QuickBooks connection status component
 */
export function attachQuickBooksConnectionListeners(
  container: HTMLElement,
  callbacks: QuickBooksConnectionStatusCallbacks,
): void {
  const connectBtn = container.querySelector('#qb-connect-btn');
  connectBtn?.addEventListener('click', callbacks.onConnect);

  const disconnectBtn = container.querySelector('#qb-disconnect-btn');
  disconnectBtn?.addEventListener('click', callbacks.onDisconnect);

  const reconnectBtn = container.querySelector('#qb-reconnect-btn');
  reconnectBtn?.addEventListener('click', callbacks.onReconnect);
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
